import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import PackageFormDialog from './PackageFormDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BILLING_LABELS = {
  monthly: 'Monatlich',
  yearly: 'Jährlich',
  weekly: 'Wöchentlich',
};

const SubscriptionsTab = ({ subscriptions, services, recurringDiscount, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const handleOpen = (sub = null) => {
    setEditingProduct(sub);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Produkt wirklich löschen?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/subscriptions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Abo gelöscht');
      onRefresh();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const getServiceNames = (serviceIds) => {
    if (!serviceIds || serviceIds.length === 0) {
      return <Badge variant="outline" className="bg-green-50 text-green-700">Alle Services</Badge>;
    }
    return serviceIds.map(id => {
      const service = services.find(s => s.id === id);
      return service ? <Badge key={id} className="mr-1">{service.name}</Badge> : null;
    });
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle data-testid="subscriptions-title">Abo-Templates ({subscriptions.length})</CardTitle>
            <CardDescription>Erstellen Sie hier Abo-Vorlagen. Kunden sehen diese im Shop.</CardDescription>
          </div>
          <Button onClick={() => handleOpen(null)} data-testid="add-subscription-btn">
            <Plus className="w-4 h-4 mr-2" />
            Neues Abo
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Credits</TableHead>
                <TableHead>Preis</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Erlaubte Services</TableHead>
                <TableHead>Aktiv</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                    Noch keine Abo-Templates erstellt
                  </TableCell>
                </TableRow>
              ) : (
                subscriptions.map((sub) => (
                  <TableRow key={sub.id} data-testid={`subscription-row-${sub.id}`}>
                    <TableCell className="font-medium">{sub.name}</TableCell>
                    <TableCell>{sub.credits_total} Credits</TableCell>
                    <TableCell>CHF {(sub.price || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={sub.billing_cycle !== 'once' ? 'default' : 'outline'}>
                        {BILLING_LABELS[sub.billing_cycle] || '10er-Abo'}
                      </Badge>
                    </TableCell>
                    <TableCell>{getServiceNames(sub.allowed_service_ids)}</TableCell>
                    <TableCell>
                      <Badge variant={sub.is_active !== false ? 'default' : 'secondary'}>
                        {sub.is_active !== false ? 'Ja' : 'Nein'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => handleOpen(sub)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(sub.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PackageFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingProduct={editingProduct}
        productType="subscription"
        services={services}
        recurringDiscount={recurringDiscount}
        onSaved={() => { setDialogOpen(false); onRefresh(); }}
      />
    </>
  );
};

export default SubscriptionsTab;
